import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    // Persist session in localStorage
    storage: window.localStorage,
    // Auto refresh session
    autoRefreshToken: true,
    // Persist session across browser tabs
    persistSession: true,
    // Detect session in URL (for magic links)
    detectSessionInUrl: true,
    // Flow type for better session handling
    flowType: 'pkce'
  }
});

// Types
export interface Profile {
  id: string;
  username: string;
  smoker_type: 'pipe' | 'cigar' | 'both';
  pipe_shapes?: string[];
  avatar_url?: string;
  weight_unit?: 'grams' | 'ounces';
  event_notifications_enabled?: boolean;
  event_notification_radius_km?: number;
  is_profile_public?: boolean;
  bio?: string;
  created_at: string;
}

export interface Tobacco {
  id: string;
  name: string;
  type: 'pipe' | 'cigar';
  brand?: string;
  flavor_tags: string[];
  strength?: string;
  origin?: string;
  image_url?: string;
  cut?: string;
  created_at: string;
  avg_rating?: number | null;
  review_count?: number | null;
  blend_type?: string | null;
  flavor_notes?: string[] | null;
  manufacturer?: string | null;
  origin_country?: string | null;
  description?: string | null;
}

export interface CheckIn {
  id: string;
  user_id: string;
  tobacco_id?: string;
  tobacco?: Tobacco;
  venue_id?: string;
  venue?: Venue;
  rating: number;
  notes?: string;
  pairing_alcoholic?: string;
  pairing_non_alcoholic?: string;
  drink_pairing?: string;
  weather?: {
    temperature?: number;
    humidity?: number;
    season?: string;
  };
  pipe_shape_used?: string;
  pipe_make?: string;
  outdoor_seating?: boolean;
  covered_seating?: boolean;
  heaters?: boolean;
  photo_url?: string;
  // Cellar references
  cellar_tobacco_id?: string;
  cellar_tobacco?: CellarTobacco;
  cellar_pipe_id?: string;
  cellar_pipe?: CellarPipe;
  cellar_lighter_id?: string;
  cellar_lighter?: CellarLighter;
  // Manual entry fallbacks
  tobacco_manual?: string;
  pipe_manual?: string;
  lighter_manual?: string;
  latitude?: number;
  longitude?: number;
  is_public?: boolean;
  location_name?: string;
  created_at: string;
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon_url?: string;
}

export interface Venue {
  id: string;
  name: string;
  type: 'lounge' | 'shop';
  address: string;
  latitude?: number;
  longitude?: number;
  outdoor_smoking?: boolean;
  covered_area?: boolean;
  heaters?: boolean;
  image_url?: string;
}

// Drinks (pairing targets)
export interface Drink {
  id: string;
  name: string;
  type?: string; // e.g., beer, whiskey, coffee, tea, rum
  flavor_tags?: string[];
  origin?: string;
  description?: string;
  image_url?: string;
}

export interface DrinkRecommendation {
  drink: Drink;
  score: number; // 0..1 similarity
  reasoning?: string; // optional short explanation
}

// Cellar Types
export interface CellarTobacco {
  id: string;
  user_id: string;
  brand: string;
  blend_name: string;
  tobacco_id?: string | null;
  blend_ref_id?: string | null;
  amount: number;
  unit: 'grams' | 'ounces';
  purchase_date: string;
  tinned_date?: string; // Date when tobacco was tinned/made
  is_opened?: boolean; // Keep for backward compatibility
  status: 'sealed' | 'opened' | 'finished';
  storage_type?: 'tin' | 'jar' | 'pouch' | 'bulk';
  opened_date?: string; // Date when tobacco was opened
  notes?: string;
  image_url?: string;
  batch_id?: string; // optional grouping id for multiples
  reminder_at?: string; // optional ISO timestamp for reminder
  reminder_notes?: string; // optional user notes for reminder
  created_at: string;
}

export interface CellarPipe {
  id: string;
  user_id: string;
  maker: string;
  shape: string;
  style?: string;
  purchase_date: string;
  last_cleaned_date?: string;
  image_url?: string;
  created_at: string;
}

export interface CellarLighter {
  id: string;
  user_id: string;
  type: 'lighter' | 'matches';
  brand: string;
  style?: string;
  colour?: string;
  purchase_date: string;
  image_url?: string;
  created_at: string;
}

// Event types
export interface Event {
  id: string;
  creator_id: string;
  venue_id?: string | null;
  title: string;
  description: string;
  venue_name?: string;
  latitude?: number;
  longitude?: number;
  photo_url?: string; // Added for event background photo
  start_datetime: string;
  end_datetime?: string;
  max_attendees?: number;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  venue?: Venue;
  creator?: {
    username: string;
  };
  responses?: EventResponse[];
}

export interface EventResponse {
  id: string;
  event_id: string;
  user_id: string;
  response_type: 'attending' | 'interested' | 'not_interested';
  created_at: string;
  updated_at: string;
}

// Map-related types
export interface NearbyCheckIn {
  id: string;
  user_id: string;
  latitude: number;
  longitude: number;
  location_name?: string;
  photo_url?: string;
  outdoor_seating?: boolean;
  covered_seating?: boolean;
  heaters?: boolean;
  created_at: string;
  username: string;
  is_profile_public?: boolean;
  tobacco_brand?: string;
  tobacco_blend?: string;
  tobacco_manual?: string;
  pipe_maker?: string;
  pipe_shape?: string;
  pipe_manual?: string;
}

// Wish list types
export interface WishTobacco {
  id: string;
  user_id: string;
  brand: string;
  blend_name: string;
  image_url?: string;
  notes?: string;
  // Phase 1 additions (already in database)
  blend_ref_id?: string;
  matched_confidence?: number;
  stock_check_enabled?: boolean;
  notify_on_stock?: boolean;
  last_notified_at?: string;
  created_at: string;
}

// Phase 2: Retailer sources for quick-search links
export interface RetailerSource {
  id: string;
  name: string;
  region?: string;
  search_template: string;
  logo_url?: string;
  enabled: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// Notifications
export interface Notification {
  id: string;
  user_id: string;
  type: 'event_nearby' | 'event_invite' | 'friend_request' | 'system';
  title: string;
  message: string;
  data?: {
    event_id?: string;
    event_title?: string;
    event_start?: string;
    distance_km?: number;
    creator_username?: string;
    venue_name?: string;
    latitude?: number;
    longitude?: number;
    connection_id?: string;
    requester_id?: string;
    requester_username?: string;
    accepter_id?: string;
    accepter_username?: string;
    [key: string]: any;
  };
  read: boolean;
  created_at: string;
}

// User Connections
export interface UserConnection {
  id: string;
  user_id: string;
  target_user_id: string;
  status: 'pending' | 'accepted' | 'blocked';
  created_at: string;
  updated_at: string;
}

export interface ConnectionWithProfile {
  connection_id: string;
  user_id: string;
  username: string;
  avatar_url?: string;
  smoker_type: 'pipe' | 'cigar' | 'both';
  status: 'pending' | 'accepted' | 'blocked';
  is_requester: boolean;
  created_at: string;
}

// Badge System Types
export interface BadgeDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
  criteria: Record<string, any>;
  created_at: string;
}

export interface UserBadge {
  badge_id: string;
  badge_name: string;
  badge_description: string;
  badge_icon: string;
  badge_category: string;
  badge_rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
  earned_at: string;
}

export interface UserActivity {
  activity_type: string;
  activity_data: Record<string, any>;
  created_at: string;
}

